import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { isWasteProduct } from '@/shared/domain/product/isWasteProduct.ts';
import { isWasteFreightType } from '@/shared/domain/product/isWasteFreightType.ts';
import { isHazardousWasteProduct } from '@/shared/domain/product/isHazardousWasteProduct.ts';
import { TextFieldInput } from '@/shared/inputs/TextFieldInput.tsx';
import { DisposalPurposeInput } from '@/shared/inputs/DisposalPurposeInput.tsx';
import { FreightTypeInput } from '@/shared/inputs/FreightTypeInput.tsx';
import { VehicleClassesSelectionInput } from '@/shared/inputs/VehicleClassesSelectionInput.tsx';
import { Fieldset, FieldsetBody, FieldsetColumn } from '../components/Fieldset.tsx';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { usePositionFormQuantities } from '@/shared/forms/AutomaticProductsForm/hooks/usePositionFormQuantities.ts';

export function PositionProductDetailsFieldset() {
    const { t } = useTranslation();
    const methods = useFormContext<AutomaticProductsFormValues>();
    const { register, watch } = methods;
    const { formRules } = useAutomaticFormContext();
    const { onVehicleClassChange } = usePositionFormQuantities(methods);
    const [productType, freightType] = watch(['productType', 'freightType']);

    const isTransportOnly = isTransportProduct(productType);
    const showDisposalPurpose = isWasteProduct(productType) || isWasteFreightType(freightType);
    const showDisposalCertificate = isHazardousWasteProduct(productType) || freightType === 'HAZARDOUS_WASTE';

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.productDetails')}
            dataTest="position-product-details-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-2 @2xl:grid-cols-8 grid-cols-1">
                <FieldsetColumn
                    className="@2xl:col-span-8 col-span-full"
                    label={t('forms.automaticProductsForm.labels.materialDescription')}
                >
                    <TextFieldInput
                        {...register('productName')}
                        size="lg"
                        placeHolder={t('forms.automaticProductsForm.fields.productName.placeholder')}
                        dataTest="product-name-input"
                    />
                </FieldsetColumn>

                <FieldsetColumn
                    className="@2xl:col-span-8 col-span-full"
                    label={t('forms.automaticProductsForm.labels.freightType')}
                >
                    <FreightTypeInput
                        {...register('freightType')}
                        disabled={formRules?.freightType?.disabled || !isTransportOnly}
                        dataTest="freight-type-input"
                    />
                </FieldsetColumn>

                {showDisposalPurpose && (
                    <FieldsetColumn
                        className="@2xl:col-span-8 col-span-full"
                        label={t('forms.automaticProductsForm.labels.disposalPurpose')}
                    >
                        <DisposalPurposeInput
                            {...register('disposalPurpose')}
                            disabled={formRules?.disposalPurpose?.disabled || isWasteProduct(productType)}
                            variant="lg"
                            dataTest="disposal-purpose-input"
                        />
                    </FieldsetColumn>
                )}

                {showDisposalCertificate && (
                    <FieldsetColumn
                        className="@2xl:col-span-8 col-span-full"
                        label={t('forms.automaticProductsForm.labels.disposalCertificateNumber')}
                    >
                        <TextFieldInput
                            {...register('disposalCertificateNumber')}
                            size="lg"
                            placeHolder={t('forms.francoForm.fields.disposalCertificate.placeholder')}
                            dataTest="disposal-certificate-number-input"
                        />
                    </FieldsetColumn>
                )}

                <FieldsetColumn
                    className="@2xl:col-span-8 col-span-full"
                    label={t('forms.automaticProductsForm.labels.vehicleClass')}
                >
                    <VehicleClassesSelectionInput
                        {...register('vehicleClass')}
                        onVehicleClassChange={onVehicleClassChange}
                    />
                </FieldsetColumn>
            </FieldsetBody>
        </Fieldset>
    );
}
